import {
  ArrowUpRight,
  ChevronUp,
  MoveDown,
  MoveUp,
  PlusCircle,
} from "lucide-react";
import Chart from "./_misc/bar-chart";
import { useAdminModals } from "@/zustand/store";
import {
  useGetAdminStats,
  useGetAllDeposits,
  useGetOverview,
} from "@/hooks/use-queries";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { formatAmount } from "@/lib/utils";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { data: adminStats } = useGetAdminStats();

  const { data: recentTransactions } = useGetAllDeposits({
    page: 1,
    limit: 5,
  });
  const { data: chartData } = useGetOverview({
    startDate: moment().subtract(20, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });

  const { data: lastWeekTransactions } = useGetOverview({
    startDate: moment().subtract(14, "days").format("YYYY-MM-DD"),
    endDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
  });

  return (
    <div>
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-3 grid grid-cols-3 gap-4">
          {/* Create User */}
          <button
            onClick={() => useAdminModals.setState({ createUserIsOpen: true })}
            className="border-primary-500 hover:bg-primary-100/70 bg-primary-100/40 flex flex-col items-center gap-2.5 rounded-lg border border-dashed p-4 transition-all duration-200"
          >
            <img className="size-10" src="/icons/create-user.svg" alt="" />
            <div className="flex items-center gap-2">
              <PlusCircle size={15} />
              <span className="text-primary-500 mb-1">Create User</span>
            </div>
          </button>

          {/* Total Users */}
          <div className="text-primary-500 flex flex-col justify-between gap-4 rounded-lg bg-white px-4 py-2.5">
            <div className="flex items-center justify-between">
              <img
                className="size-12"
                src="/icons/users-shaded.svg"
                alt="shaded user icon"
              />
              <div className="flex flex-col items-end justify-between gap-2">
                <span className="leading-4 font-semibold">Total Users</span>
                <span className="text-2xl leading-4 font-medium">
                  {adminStats?.users.totalUsers}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-6 text-xs">
              <div>
                <p className="text-primary-green font-semibold">Active</p>
                <p className="font-bold">{adminStats?.users.activeUsers}</p>
              </div>
              <div>
                <p className="text-primary-blue font-semibold">Verified Card</p>
                <p className="font-bold">{adminStats?.users.usersWithCards}</p>
              </div>
              <div>
                <p className="text-primary-red font-semibold">Inactive</p>
                <p className="font-bold">
                  {" "}
                  {isNaN(
                    adminStats?.users.totalUsers -
                      adminStats?.users.activeUsers,
                  )
                    ? 0
                    : parseInt(
                        adminStats?.users.totalUsers -
                          adminStats?.users.activeUsers,
                        10,
                      )}
                </p>
              </div>
            </div>
          </div>

          {/* Today's Activities */}
          <div className="text-primary-500 col-span-3 flex flex-col justify-between gap-4 rounded-lg bg-white px-4 py-4">
            <h2 className="text-lg leading-4 font-bold">Today's Activities</h2>
            {/* pending deposits */}
            <div className="mt-2 flex gap-4 text-white *:min-w-60">
              <div
                className="relative flex justify-between rounded px-2 py-4"
                style={{
                  background:
                    "linear-gradient(258.35deg, #F86893 -0.65%, #DA1CC7 96.47%)",
                }}
              >
                <button
                  onClick={() => navigate("/admin-dashboard/transactions")}
                  className="absolute top-2 right-2 flex size-5 items-center justify-center rounded-full border border-white hover:border-white/50 hover:text-white/50"
                >
                  <ArrowUpRight size={15} />
                </button>
                <div className="flex items-center gap-4">
                  <span className="text-4xl font-bold">
                    {adminStats?.stats?.deposits?.pending?.count || 0}
                  </span>
                  <div className="flex flex-col gap-2">
                    <p className="leading-3">Pending</p>
                    <p className="leading-3 font-bold">Deposit Approval</p>
                  </div>
                </div>
              </div>
              {/* frozen cards */}
              <div
                className="relative flex justify-between rounded px-2 py-4"
                style={{
                  background:
                    "linear-gradient(258.35deg, #8DD234 -0.65%, #2EAF4A 96.47%)",
                }}
              >
                <button
                  onClick={() => navigate("/admin-dashboard/cards")}
                  className="absolute top-2 right-2 flex size-5 items-center justify-center rounded-full border border-white hover:border-white/50 hover:text-white/50"
                >
                  <ArrowUpRight size={15} />
                </button>
                <div className="flex items-center gap-4">
                  <span className="text-4xl font-bold">
                    {adminStats?.frozenCards}
                  </span>
                  <div className="flex flex-col gap-2">
                    <p className="leading-3">Frozen</p>
                    <p className="leading-3 font-bold">Cards</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-primary-500 flex flex-col gap-4 rounded-lg bg-white px-2.5 py-4">
          <h2 className="text-lg leading-3 font-bold">Transaction Balance</h2>
          <div className="flex flex-1 grow flex-col justify-between">
            <div className="flex grow items-center gap-6">
              <div className="border-primary-50/30 text-primary-green flex size-10 shrink-0 items-center justify-center rounded-md border-[3px]">
                <MoveDown strokeWidth={3} size={15} />
              </div>
              <div className="flex flex-col justify-between gap-2">
                <span className="text-primary-green text-sm leading-2 font-medium">
                  Total User Balance
                </span>
                <span className="text-2xl leading-4 font-medium">
                  ${formatAmount(adminStats?.users.totalBalance)}
                </span>
              </div>
            </div>
            <div className="bg-primary-50/30 h-[1px] w-full" />
            <div className="flex grow items-center gap-6">
              <div className="border-primary-50/30 text-primary-red flex size-10 shrink-0 items-center justify-center rounded-md border-[3px]">
                <MoveUp strokeWidth={3} size={15} />
              </div>
              <div className="flex flex-col justify-between gap-2">
                <span className="text-primary-red text-sm leading-2 font-medium">
                  Total Withdrawal
                </span>
                <span className="text-2xl leading-4 font-medium">
                  ${formatAmount(adminStats?.deposits[0].totalWithdrawals)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* overview balance + transaction activity */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        {/* overview balance */}
        <div className="text-primary-500 flex flex-col gap-4 overflow-hidden rounded-lg bg-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg leading-3 font-bold">Overview Balance</h2>
            </div>
            <p className="text-primary-50 text-sm">
              {moment().subtract(20, "days").format("MM/DD/YYYY")} -{" "}
              {moment().format("MM/DD/YYYY")}
            </p>
          </div>
          <div className="flex grow flex-col justify-between">
            <div className="flex grow items-center justify-between">
              <p>
                Last Week{" "}
                <span className="text-primary-green font-medium">
                  $
                  {lastWeekTransactions
                    ?.reduce((total, data) => total + data.totalAmount, 0)
                    .toFixed(2)}
                </span>
              </p>

              <div className="flex items-center gap-2">
                <span className="text-3xl font-semibold">
                  $
                  {chartData
                    ?.reduce((total, data) => total + data.totalAmount, 0)
                    .toFixed(2)}
                </span>
                <span className="text-primary-green text-xl font-medium">
                  {(
                    ((chartData?.reduce(
                      (total, data) => total + data.totalAmount,
                      0,
                    ) -
                      lastWeekTransactions?.reduce(
                        (total, data) => total + data.totalAmount,
                        0,
                      )) /
                      lastWeekTransactions?.reduce(
                        (total, data) => total + data.totalAmount,
                        0,
                      )) *
                    100
                  ).toFixed(2) || 0}
                  %
                </span>
                <ChevronUp className="text-primary-green" size={15} />
              </div>
            </div>
            {chartData?.length > 0 ? (
              <Chart data={chartData} />
            ) : (
              <div className="flex h-[300px] items-center justify-center">
                No Transactions Yet
              </div>
            )}
          </div>
        </div>

        <div className="text-primary-500 flex flex-col gap-4 rounded-lg bg-white py-4">
          <div className="flex items-center justify-between px-2.5">
            <div className="space-y-2">
              <h2 className="text-lg leading-3 font-bold">
                Transaction Activity
              </h2>
              <p className="text-sm leading-3 font-medium">
                The latest transactions Activity
              </p>
            </div>
            <div className="bg-primary-500 rounded-full px-4 py-2.5 text-white">
              Today
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            {recentTransactions?.data.map((transaction, index) => (
              <div
                key={index}
                className="grid grid-cols-6 items-center px-2.5 py-2.5 hover:cursor-pointer hover:shadow-[0px_2.9px_27.59px_0px_#6418C338]"
              >
                <div className="col-span-2 flex items-center gap-2">
                  <div className="border-primary-50/30 text-primary-green flex size-10 items-center justify-center rounded-md border-[3px]">
                    {transaction.type === "deposit" ? (
                      <MoveDown
                        strokeWidth={3}
                        className="text-primary-green"
                        size={15}
                      />
                    ) : (
                      <MoveUp
                        strokeWidth={3}
                        className="text-primary-red"
                        size={15}
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="leading-3 font-semibold">
                      {transaction.type.charAt(0).toUpperCase() +
                        transaction.type.slice(1)}
                    </p>
                    <p className="text-sm leading-2.5">
                      {moment(transaction.createdAt).format("hh:mm:ss A")}
                    </p>
                  </div>
                </div>
                <div className="col-span-2 space-y-2">
                  <p className="leading-3 font-semibold">
                    {transaction.firstName + " " + transaction.lastName}
                  </p>
                  <p className="text-sm leading-2.5">#{transaction.id}</p>
                </div>
                <p className="text-sm leading-2.5">
                  ${formatAmount(transaction.amount)}
                </p>
                <span
                  className={`${transaction.status === "failed" ? "text-primary-red" : transaction.status === "completed" ? "text-primary-green" : "text-primary-50"} font-medium`}
                >
                  {transaction.status.charAt(0).toUpperCase() +
                    transaction.status.slice(1).toLowerCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
